import { BooleanInput, coerceBooleanProperty } from "@angular/cdk/coercion";
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from "@angular/core";
import { Checkin, CheckinStatus } from "@dtm-frontend/shared/ui/tactical";
import { FunctionUtils, LocalComponentStore } from "@dtm-frontend/shared/utils";
import { UntilDestroy } from "@ngneat/until-destroy";
import turfCenter from "@turf/center";
import { Polygon, points as turfPoints } from "@turf/helpers";
import { Cartesian3, Cartographic, Color, CustomDataSource, PolygonGraphics, PolylineDashMaterialProperty, PolylineGraphics } from "cesium";
import { Observable, distinctUntilChanged, map, shareReplay } from "rxjs";
import { CesiumMapService, CoordinateConverter } from "../..";
import { CameraHelperService } from "../../services/camera-helper.service";
import { createCesiumDashPattern } from "../../utils/create-cesium-dash-pattern";

const DEFAULT_FILL_OPACITY = 0.5;
const DEFAULT_AREA_COLOR = "#1477d5"; // $legacy-color-secondary-700
const DEFAULT_OUTLINE_DASH_LENGTH = 20;
const DEFAULT_OUTLINE_DASH_PATTERN = createCesiumDashPattern("------------");
const DEFAULT_OUTLINE_WIDTH = 3;
const CHECKIN_AREA_FILL_COLOR = Color.fromCssColorString(DEFAULT_AREA_COLOR).withAlpha(DEFAULT_FILL_OPACITY); // $legacy-color-secondary-700
const LABELS_SHOWING_DISTANCE_IN_METERS = 20000;

const DEFAULT_OUTLINE_MATERIAL = new PolylineDashMaterialProperty({
    color: Color.fromCssColorString(DEFAULT_AREA_COLOR),
    dashLength: DEFAULT_OUTLINE_DASH_LENGTH,
    dashPattern: DEFAULT_OUTLINE_DASH_PATTERN,
});

interface CheckinAreaEntity {
    id: string;
    positions: Cartesian3[];
    centerPosition: Cartesian3 | undefined;
    data: Checkin;
}

interface CheckinAreaLayerComponentState {
    isProcessing: boolean;
    selectedCheckin: Checkin | undefined;
    checkins: Checkin[] | undefined;
    isSelectionEnabled: boolean;
    shouldShowSimplifiedLabels: boolean;
}

@UntilDestroy()
@Component({
    selector: "dtm-map-lib-checkin-area-layer",
    templateUrl: "./checkin-area-layer.component.html",
    styleUrls: ["../../../shared/styles/map-segment-pin.scss"],
    styles: [
        `
            .segment-pin.selectable {
                pointer-events: auto;
                cursor: pointer;
            }
        `,
    ],
    providers: [LocalComponentStore],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckinAreaLayerComponent {
    @Input() public set isProcessing(value: BooleanInput) {
        this.localStore.patchState({ isProcessing: coerceBooleanProperty(value) });
    }
    @Input() public set selectedCheckin(value: Checkin | undefined) {
        this.localStore.patchState({ selectedCheckin: value });
    }
    @Input() public set checkins(value: Checkin[] | undefined) {
        this.updateCheckinAreaEntities(value ?? []);
        this.localStore.patchState({ checkins: value });
    }
    @Input() public set isSelectionEnabled(value: BooleanInput) {
        this.localStore.patchState({ isSelectionEnabled: coerceBooleanProperty(value) });
    }
    @Input() public set shouldShowSimplifiedLabels(value: BooleanInput) {
        this.localStore.patchState({ shouldShowSimplifiedLabels: coerceBooleanProperty(value) });
    }

    @Output() public checkinSelect = new EventEmitter<string>();

    protected readonly isProcessing$ = this.localStore.selectByKey("isProcessing");
    protected readonly selectedCheckin$ = this.localStore.selectByKey("selectedCheckin");
    protected readonly areLabelsVisible$ = this.cameraHelperService.postRender$.pipe(
        map(() => this.cesiumService.getViewer().camera.positionCartographic.height <= LABELS_SHOWING_DISTANCE_IN_METERS),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
    );
    protected readonly isSelectionEnabled$ = this.localStore.selectByKey("isSelectionEnabled");
    protected readonly shouldShowSimplifiedLabels$ = this.localStore.selectByKey("shouldShowSimplifiedLabels");
    protected readonly checkinAreaEntities$ = this.getCheckinAreaEntityStream();

    protected CheckinStatus = CheckinStatus;

    private readonly checkinAreaLayerDataSource = new CustomDataSource();

    constructor(
        private readonly localStore: LocalComponentStore<CheckinAreaLayerComponentState>,
        private readonly cesiumService: CesiumMapService,
        private readonly coordinateConverter: CoordinateConverter,
        private readonly cameraHelperService: CameraHelperService
    ) {
        this.localStore.setState({
            isProcessing: true,
            selectedCheckin: undefined,
            checkins: undefined,
            shouldShowSimplifiedLabels: false,
            isSelectionEnabled: false,
        });

        this.cesiumService.getViewer().dataSources.add(this.checkinAreaLayerDataSource);
    }

    private updateCheckinAreaEntities(checkins: Checkin[]): void {
        const existingIds = new Set(this.checkinAreaLayerDataSource.entities.values.map(({ id }) => id));
        const newIds = new Set(checkins.map(({ id }) => id));

        existingIds.forEach((id) => {
            if (!newIds.has(id)) {
                this.checkinAreaLayerDataSource.entities.removeById(id);
            }
        });

        checkins.forEach((checkin) => {
            const area = checkin.geometry as Polygon;
            if (!area) {
                return;
            }

            const entity = this.checkinAreaLayerDataSource.entities.getOrCreateEntity(checkin.id);
            const positions = Cartesian3.fromDegreesArray(area.coordinates[0].flat());
            entity.polygon ??= new PolygonGraphics({
                hierarchy: positions,
                material: CHECKIN_AREA_FILL_COLOR,
                height: 0,
            });

            entity.polyline ??= new PolylineGraphics({
                positions,
                material: DEFAULT_OUTLINE_MATERIAL,
                width: DEFAULT_OUTLINE_WIDTH,
                zIndex: 1,
            });
        });

        this.cesiumService.getScene().requestRender();
    }

    private getCheckinAreaEntityStream(): Observable<CheckinAreaEntity[]> {
        return this.localStore.selectByKey("checkins").pipe(
            map((checkins) => {
                if (!checkins) {
                    return [];
                }

                return checkins
                    .map((checkin) => {
                        const area = checkin.geometry as Polygon;
                        if (!area) {
                            return;
                        }

                        const areaPoints = turfPoints(area.coordinates[0]);
                        const center = turfCenter(areaPoints);
                        const centerCartesian: Cartesian3 = Cartographic.toCartesian(
                            this.coordinateConverter.degreesToCartographic(center.geometry.coordinates[0], center.geometry.coordinates[1])
                        );

                        const entity: CheckinAreaEntity = {
                            id: checkin.id,
                            positions: Cartesian3.fromDegreesArray(area.coordinates[0].flat()),
                            centerPosition: centerCartesian,
                            data: checkin,
                        };

                        return entity;
                    })
                    .filter(FunctionUtils.isTruthy);
            })
        );
    }

    protected getCheckinClass(checkinData: Checkin, selectedChecking?: Checkin) {
        return {
            active: checkinData.status === CheckinStatus.InRealization,
            deactivated: checkinData.status === CheckinStatus.Expired,
            waiting: checkinData.status === CheckinStatus.Submitted,
            selected: checkinData.id === selectedChecking?.id,
        };
    }

    protected toggleCheckingSelection(checkin: Checkin): void {
        const currentCheckin = this.localStore.selectSnapshotByKey("selectedCheckin");
        if (currentCheckin?.id === checkin.id) {
            this.checkinSelect.emit(undefined);

            return;
        }
        this.checkinSelect.emit(checkin.id);
    }
}
